import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkAndUpdateExpiryStatus } from '../../utils/expiryChecker';

export default function SubscriptionPage() {
  const [companyName, setCompanyName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [email, setEmail] = useState('');
  const [moduleMap, setModuleMap] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const storedEmail = localStorage.getItem('email');
    setEmail(storedEmail || '');

    checkAndUpdateExpiryStatus(storedEmail);

    const registry = JSON.parse(localStorage.getItem('accountRegistry') || '{}');
    const account = registry[storedEmail] || {};
    setCompanyName(account.companyName || '');
    setTenantId(account.tenantId || '');

    const auditLog = JSON.parse(localStorage.getItem(`paymentAuditLog_${storedEmail}`) || '[]');

    const grouped = {};

    auditLog.forEach((entry) => {
      const key = entry.moduleName === 'HRIS' ? 'PeopleSync' : entry.moduleName;
      if (!grouped[key]) grouped[key] = [];

      grouped[key].push({
        purchaseDate: entry.purchaseDate,
        startDate: entry.startDate,
        expiryDate: entry.expiryDate,
        billingCycle: entry.billingCycle || '',
        currency: entry.currency || 'BDT',
        amount: entry.amount || 0,
        paymentStatus: entry.paymentStatus || 'paid'
      });
    });

    setModuleMap(grouped);
  }, []);

  const handleRenew = (moduleName, billingCycle, currency, amount) => {
    const selectedModules = [{ name: moduleName, price: amount }];
    navigate('/payment', {
      state: {
        selectedModules,
        billingCycle,
        currency,
        tenantId,
        companyName
      }
    });
  };

  // ✅ Replaced logic: Next Bill Date = Expiry Date
  const computeNextBillDate = (expiryDate) => {
    return new Date(expiryDate).toLocaleString('en-BD', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatLocalDateTime = (isoString) => {
    if (!isoString) return '—';
    return new Date(isoString).toLocaleString('en-BD', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-indigo-700 mb-4">Subscription Plan</h1>
      {(companyName || tenantId) && (
        <div className="mb-4 text-sm text-gray-600 italic">
          {companyName && <div>Company Name: {companyName}</div>}
          {tenantId && <div>Tenant ID: {tenantId}</div>}
        </div>
      )}

      {Object.keys(moduleMap).length === 0 ? (
        <div className="border rounded-lg p-4 bg-white shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800">No subscription records found</h3>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(moduleMap).map(([moduleName, records]) => {
            const sorted = records.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
            const first = records[records.length - 1];
            const latest = sorted[0];
            const grandTotal = latest.amount;

            return (
              <div
                key={moduleName}
                className="border rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition"
              >
                <h3 className="text-xl font-semibold text-indigo-700 mb-2">{moduleName}</h3>
                <p className="text-sm text-gray-600">
                  First Subscribed On: {first.purchaseDate || '—'}
                </p>
                <p className="text-sm text-gray-600">
                  Last Renewed On: {formatLocalDateTime(latest.startDate)}
                </p>
                <p className="text-sm text-gray-600">
                  Expires On: {formatLocalDateTime(latest.expiryDate)}
                </p>
                <p className="text-sm text-gray-600">
                  Billing Cycle:{' '}
                  {latest.billingCycle
                    ? latest.billingCycle.charAt(0).toUpperCase() + latest.billingCycle.slice(1)
                    : '—'}
                </p>
                <p className="text-sm text-gray-600">
                  Amount Paid (excluding VAT & AIT): {latest.currency} {grandTotal.toFixed(2)}
                </p>
                <p className="text-sm text-gray-600">
                  Next Bill: {latest.currency} {grandTotal.toFixed(2)} on{' '}
                  {computeNextBillDate(latest.expiryDate)}
                </p>
                <p className="text-sm text-gray-600">
                  Status: {latest.paymentStatus === 'paid' ? '✅ Paid' : '❌ Unpaid'}
                </p>

                {latest.paymentStatus === 'unpaid' && (
                  <div className="mt-3">
                    <button
                      onClick={() =>
                        handleRenew(moduleName, latest.billingCycle, latest.currency, latest.amount)
                      }
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Renew {latest.billingCycle} — {latest.currency} {grandTotal.toFixed(2)}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
